/**
 * HeaderPropertiesEditor Component
 * 
 * Property editor for Header block type.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderBlockProps } from '@/types/menuBuilder';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface HeaderPropertiesEditorProps {
  props: HeaderBlockProps;
  onUpdate: (props: Partial<HeaderBlockProps>) => void;
}

export const HeaderPropertiesEditor = memo(function HeaderPropertiesEditor({
  props,
  onUpdate,
}: HeaderPropertiesEditorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Variant Selection */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.variant', 'Variant')}</Label>
        <Select
          value={props.variant}
          onValueChange={(value) =>
            onUpdate({ variant: value as HeaderBlockProps['variant'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="with-cover">
              {t('menuBuilder.withCover', 'With Cover')}
            </SelectItem>
            <SelectItem value="logo-only">
              {t('menuBuilder.logoOnly', 'Logo Only')}
            </SelectItem>
            <SelectItem value="simple">
              {t('menuBuilder.simple', 'Simple')}
            </SelectItem>
            <SelectItem value="minimal">
              {t('menuBuilder.minimal', 'Minimal')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Display Options */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium">
          {t('menuBuilder.displayOptions', 'Display Options')}
        </h4>

        <div className="flex items-center justify-between">
          <Label htmlFor="showLogo">
            {t('menuBuilder.showLogo', 'Show Logo')}
          </Label>
          <Switch
            id="showLogo"
            checked={props.showLogo}
            onCheckedChange={(checked) => onUpdate({ showLogo: checked })}
          />
        </div>

        {/* Logo Size */}
        {props.showLogo && (
          <div className="space-y-2 pl-4 border-l-2 border-muted">
            <Label>{t('menuBuilder.logoSize', 'Logo Size')}</Label>
            <Select
              value={props.logoSize || 'md'}
              onValueChange={(value) => 
                onUpdate({ logoSize: value as HeaderBlockProps['logoSize'] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">{t('menuBuilder.sizes.sm', 'Small')}</SelectItem>
                <SelectItem value="md">{t('menuBuilder.sizes.md', 'Medium')}</SelectItem>
                <SelectItem value="lg">{t('menuBuilder.sizes.lg', 'Large')}</SelectItem>
                <SelectItem value="xl">{t('menuBuilder.sizes.xl', 'Extra Large')}</SelectItem>
                <SelectItem value="custom">{t('menuBuilder.sizes.custom', 'Custom')}</SelectItem>
              </SelectContent>
            </Select>

            {props.logoSize === 'custom' && (
              <div className="pt-2">
                <Label className="text-xs">{t('menuBuilder.customSizePx', 'Custom Size (px)')}</Label>
                <Input
                  type="number"
                  value={props.customLogoSize || 80}
                  onChange={(e) => onUpdate({ customLogoSize: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
            )}

            {/* Logo Padding - only for logo-only variant */}
            {props.variant === 'logo-only' && (
              <div className="pt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">{t('menuBuilder.logoPadding', 'Padding')}</Label>
                  <span className="text-xs text-muted-foreground">{props.logoPadding ?? 4}px</span>
                </div>
                <Slider
                  value={[props.logoPadding ?? 4]}
                  min={0}
                  max={40}
                  step={2}
                  onValueChange={([value]) => onUpdate({ logoPadding: value })}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="showRestaurantName">
            {t('menuBuilder.showRestaurantName', 'Show Restaurant Name')}
          </Label>
          <Switch
            id="showRestaurantName"
            checked={props.showRestaurantName}
            onCheckedChange={(checked) => onUpdate({ showRestaurantName: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="showShareButton">
            {t('menuBuilder.showShareButton', 'Show Share Button')}
          </Label>
          <Switch
            id="showShareButton"
            checked={props.showShareButton}
            onCheckedChange={(checked) => onUpdate({ showShareButton: checked })}
          />
        </div>
      </div>

      {/* Cover-specific options */}
      {props.variant === 'with-cover' && (
        <>
          {/* Custom Height */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.customHeight', 'Custom Height (px)')}</Label>
            <Input
              type="number"
              min={100}
              max={400}
              value={props.customHeight || 200}
              onChange={(e) => onUpdate({ customHeight: Number(e.target.value) || 200 })}
            />
          </div>

          {/* Logo Position */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.logoPosition', 'Logo Position')}</Label>
            <Select
              value={props.logoPosition || 'center'}
              onValueChange={(value) =>
                onUpdate({ logoPosition: value as HeaderBlockProps['logoPosition'] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">{t('common.left', 'Left')}</SelectItem>
                <SelectItem value="center">{t('common.center', 'Center')}</SelectItem>
                <SelectItem value="right">{t('common.right', 'Right')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Text Alignment */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.textAlignment', 'Text Alignment')}</Label>
            <Select
              value={props.textAlignment || 'center'}
              onValueChange={(value) =>
                onUpdate({ textAlignment: value as HeaderBlockProps['textAlignment'] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">{t('common.left', 'Left')}</SelectItem>
                <SelectItem value="center">{t('common.center', 'Center')}</SelectItem>
                <SelectItem value="right">{t('common.right', 'Right')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Overlay Opacity */}
          <div className="space-y-2">
            <Label>
              {t('menuBuilder.overlayOpacity', 'Overlay Opacity')}: {Math.round((props.overlayOpacity || 0.3) * 100)}%
            </Label>
            <Slider
              value={[(props.overlayOpacity || 0.3) * 100]}
              onValueChange={(value) => onUpdate({ overlayOpacity: value[0] / 100 })}
              min={0}
              max={80}
              step={5}
            />
          </div>
        </>
      )}

      {/* Colors */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="text-sm font-medium">{t('menuBuilder.colors', 'Colors')}</h4>
        
        {/* Text Color */}
        <div className="space-y-2">
          <Label>{t('menuBuilder.textColor', 'Text Color')}</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              value={props.textColor || '#ffffff'}
              onChange={(e) => onUpdate({ textColor: e.target.value })}
              className="h-10 w-12 p-1 cursor-pointer"
            />
            <Input
              type="text"
              value={props.textColor || '#ffffff'}
              onChange={(e) => onUpdate({ textColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>

        {/* Background Color */}
        <div className="space-y-2">
          <Label>{t('menuBuilder.backgroundColor', 'Background Color')}</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              value={props.backgroundColor || (props.variant === 'with-cover' ? '#f3f4f6' : '#ffffff')}
              onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
              className="h-10 w-12 p-1 cursor-pointer"
            />
            <Input
              type="text"
              value={props.backgroundColor || (props.variant === 'with-cover' ? '#f3f4f6' : '#ffffff')}
              onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default HeaderPropertiesEditor;
